import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Instrument } from '../../models/go-admin/instrument';
import { Laboratory } from '../../models/go-admin/laboratory';
import { User, UserBasic } from '../../models/go-admin/user';
import { HttpHelperService } from '../http-helper/http-helper.service';
import { Role } from '../../models/go-admin/role';

/**
 * API client for the Go Admin backend (`GOADMIN_URL`).
 * Provides CRUD operations for instruments, laboratories, users, and roles.
 */
@Injectable({
  providedIn: 'root'
})
export class GoAdminService {

  private URL = environment.GOADMIN_URL;

  http = inject(HttpClient);
  helper = inject(HttpHelperService);

  /******************************
   *  INSTRUMENT API
   ******************************/

  /** Returns the full list of registered instruments. */
  getInstrumentList$(): Observable<Instrument[]> {
    const path = `${this.URL}/instrument/list`;
    return this.http.get<Instrument[]>(path, this.helper.requestOptions());
  }

  /**
   * Returns a single instrument by its numeric ID.
   * @param id Instrument ID.
   */
  getInstrument$(id: number): Observable<Instrument> {
    const path = `${this.URL}/instrument/${id}`;
    return this.http.get<Instrument>(path, this.helper.requestOptions());
  }

  /******************************
   *  LABORATORY API
   ******************************/

  /** Returns all laboratories that have access to LC-BinBase. */
  getLaboratoryList$(): Observable<Laboratory[]> {
    const path = `${this.URL}/laboratory/list`;
    return this.http.get<Laboratory[]>(path, this.helper.requestOptions());
  }

  /**
   * Returns a single laboratory by its numeric ID.
   * @param id Laboratory ID.
   */
  getLaboratory$(id: number): Observable<Laboratory> {
    const path = `${this.URL}/laboratory/${id}`;
    return this.http.get<Laboratory>(path, this.helper.requestOptions());
  }

  /******************************
   *  USER API
   ******************************/

  /**
   * Returns a user's full record by numeric ID.
   * @param id User ID.
   */
  getUser$(id: number): Observable<User> {
    const path = `${this.URL}/user/${id}`;
    return this.http.get<User>(path, this.helper.requestOptions());
  }

  /**
   * Returns a user's full record by their login (email address).
   * @param login User login / email.
   */
  getUserByLogin$(login: string): Observable<User> {
    const path = `${this.URL}/user/login/${login}`;
    return this.http.get<User>(path, this.helper.requestOptions());
  }

  /**
   * Returns the laboratory associated with a user login.
   * @param login User login / email.
   */
  getUserLabBasic$(login: string): Observable<Laboratory> {
    const path = `${this.URL}/userlab/${login}`;
    return this.http.get<Laboratory>(path, this.helper.requestOptions());
  }

  /** Returns the full list of users as basic (non-sensitive) records. */
  getUserList$(): Observable<UserBasic[]> {
    const path = `${this.URL}/user/list`;
    console.log('Fetching user list:', path);
    return this.http.get<UserBasic[]>(path, this.helper.requestOptions());
  }

  /******************************
   *  ROLE API
   ******************************/

  /** Returns all available user roles. */
  getRoleList$(): Observable<Role[]> {
    console.log(this.URL);
    const path = `${this.URL}/role/list`;
    return this.http.get<Role[]>(path, this.helper.requestOptions());
  }

  /******************************
   *  CREATE OPERATIONS
   ******************************/

  /**
   * Creates a new laboratory record.
   * @param laboratory Laboratory data to store.
   */
  createLaboratory$(laboratory: Laboratory): Observable<Laboratory> {
    const path = `${this.URL}/laboratory`;
    return this.http.post<Laboratory>(path, laboratory, this.helper.requestOptions()).pipe(
      // Ensure all properties are properly set
      map(response => {
        // If any property is empty or undefined, use the original value
        return {
          id: response.id,
          name: response.name || laboratory.name,
          description: response.description || laboratory.description,
          disabled: response.disabled || laboratory.disabled,
        };
      })
    );
  }

  /**
   * Creates a new instrument record.
   * @param instrument Instrument data to store.
   */
  createInstrument$(instrument: Instrument): Observable<Instrument> {
    const path = `${this.URL}/instrument`;
    return this.http.post<Instrument>(path, instrument, this.helper.requestOptions()).pipe(
      // Ensure all properties are properly set
      map(response => {
        console.log('response', response);
        // If any property is empty or undefined, use the original value
        return {
          id: response.id,
          name: response.name || instrument.name,
          manufacturer: response.manufacturer || instrument.manufacturer,
          model: response.model || instrument.model,
          serial: response.serial || instrument.serial,
          description: response.description || instrument.description,
          laboratory_id: response.laboratory_id || instrument.laboratory_id,
          instrument_type: response.instrument_type || instrument.instrument_type,
          disabled: response.disabled || instrument.disabled,
          in_service: response.in_service || instrument.in_service,
        };
      })
    );
  }

  /**
   * Creates a new user record.
   * @param user User data to store.
   */
  createUser$(user: UserBasic): Observable<UserBasic> {
    const path = `${this.URL}/user`;
    return this.http.post<UserBasic>(path, user, this.helper.requestOptions()).pipe(
      // Ensure all properties are properly set
      map(response => {
        // If any property is empty or undefined, use the original value
        return {
          id: response.id,
          name: response.name || user.name,
          email: response.email || user.email,
          login: response.login || user.login,
          laboratory_id: response.laboratory_id || user.laboratory_id,
          roles: response.roles || user.roles,
          disabled: response.disabled || user.disabled,
        };
      })
    );
  }

  /******************************
   *  UPDATE OPERATIONS
   ******************************/

  /**
   * Updates an existing laboratory record.
   * @param laboratory Updated laboratory data; `id` identifies the record.
   */
  updateLaboratory$(laboratory: Laboratory): Observable<Laboratory> {
    const path = `${this.URL}/laboratory/${laboratory.id}`;
    return this.http.put<Laboratory>(path, laboratory, this.helper.requestOptions());
  }

  /**
   * Updates an existing instrument record.
   * @param instrument Updated instrument data; `id` identifies the record.
   */
  updateInstrument$(instrument: Instrument): Observable<Instrument> {
    const path = `${this.URL}/instrument/${instrument.id}`;
    return this.http.put<Instrument>(path, instrument, this.helper.requestOptions());
  }

  /**
   * Updates an existing user record.
   * @param user Updated user data; `id` identifies the record.
   */
  updateUser$(user: UserBasic): Observable<UserBasic> {
    const path = `${this.URL}/user/${user.id}`;
    return this.http.put<UserBasic>(path, user, this.helper.requestOptions());
  }

  /******************************
   *  DELETE OPERATIONS
   ******************************/

  /**
   * Deletes a laboratory record by ID.
   * @param id Laboratory ID.
   */
  deleteLaboratory$(id: number): Observable<any> {
    const path = `${this.URL}/laboratory/${id}`;
    return this.http.delete(path, this.helper.requestOptions());
  }

  /**
   * Deletes an instrument record by ID.
   * @param id Instrument ID.
   */
  deleteInstrument$(id: number): Observable<any> {
    const path = `${this.URL}/instrument/${id}`;
    return this.http.delete(path, this.helper.requestOptions());
  }

  /**
   * Deletes a user record by ID.
   * @param id User ID.
   */
  deleteUser$(id: number): Observable<any> {
    const path = `${this.URL}/user/${id}`;
    return this.http.delete(path, this.helper.requestOptions());
  }

}
